import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Archive, ArchiveRestore, MoreVertical, Pencil, Plus, Search, Settings, Trash2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Card, CardContent } from '@/components/ui/card'
import Grid from '../../components/grid'
import * as Query from '../../api/gql/query'
import * as Mutation from '../../api/gql/mutation'
import { useRouter } from '../../hooks'
import { useModuleActions } from '../../hooks/useMyPermissions'
import ScheduleListSkeleton from './ScheduleListSkeleton'
import ScheduleStatusBadge from './components/ScheduleStatusBadge'
import HealthCounts from './components/HealthCounts'
import ConfirmDialog from './components/ConfirmDialog'
import ScheduleFormDialog from './components/ScheduleFormDialog'
import ScheduleImportDialog from './components/ScheduleImportDialog'
import ScheduleSettingsDialog from './components/ScheduleSettingsDialog'
import { formatIsoDate } from './lib/format'
import { describeScheduleError } from './lib/errors'
import type { Schedule } from './types'

type Pending = { kind: 'delete' | 'archive' | 'restore'; schedule: Schedule } | null

const Schedules: React.FC = () => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const { data: meData } = useQuery(Query.ME)
  const projectId: string | undefined = meData?.me?.currentProject?.id
  const { canCreate, canEdit, canDelete, canAdmin } = useModuleActions('SCHEDULE', projectId)

  const [includeArchived, setIncludeArchived] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'all' | 'DRAFT' | 'ACTIVE' | 'ARCHIVED'>('all')
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Schedule | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data, loading, refetch } = useQuery(Query.SCHEDULES, { variables: { projectId, includeArchived }, skip: !projectId })
  const [updateSchedule] = useMutation(Mutation.UPDATE_SCHEDULE)
  const [deleteSchedule] = useMutation(Mutation.DELETE_SCHEDULE)

  const rows: Schedule[] = useMemo(() => {
    const list: Schedule[] = data?.schedules ?? []
    const q = search.trim().toLowerCase()
    return list.filter((s) => (statusFilter === 'all' || s.status === statusFilter) && (!q || s.name.toLowerCase().includes(q)))
  }, [data, statusFilter, search])

  const openDetail = (s: Schedule) => history.push(`/project/${projectId}/schedule/${s.id}`)

  const runPending = async () => {
    if (!pending) return
    setSubmitting(true)
    try {
      if (pending.kind === 'delete') {
        await deleteSchedule({ variables: { id: pending.schedule.id } })
        toastr.success('', t('Schedule_deleted'))
      } else {
        await updateSchedule({ variables: { id: pending.schedule.id, input: { status: pending.kind === 'archive' ? 'ARCHIVED' : 'ACTIVE' } } })
        toastr.success('', t(pending.kind === 'archive' ? 'Schedule_archived' : 'Schedule_restored'))
      }
      setPending(null)
      refetch().catch(() => undefined) // review fix (Minor #12): avoid an unhandled rejection on a transient network failure
    } catch (error: any) {
      toastr.error(t('Common_error'), describeScheduleError(error.message, t))
    } finally {
      setSubmitting(false)
    }
  }

  const columns = [
    { title: t('Schedule_col_name'), name: 'name', width: 260 },
    { title: t('Schedule_field_status'), name: 'status', width: 110 },
    { title: t('Schedule_col_start'), name: 'start', width: 110 },
    { title: t('Schedule_col_finish'), name: 'finish', width: 110 },
    { title: t('Schedule_activities'), name: 'activityCount', width: 90 },
    { title: t('Schedule_col_health'), name: 'stats', width: 110 },
    { title: t('Schedule_critical'), name: 'critical', width: 90 },
    { title: t('Schedule_col_updated'), name: 'updatedAt', width: 120 },
    { title: '', name: 'actions', width: 56 },
  ]
  const formatters = [
    { formatter: ({ value }: { value: string }) => <ScheduleStatusBadge status={value as any} />, for: ['status'] },
    { formatter: ({ value }: { value: string | null }) => <span className="tabular-nums">{formatIsoDate(t, value) || '—'}</span>, for: ['start', 'finish'] },
    { formatter: ({ row }: { row: Schedule }) => <HealthCounts stats={row.stats} />, for: ['stats'] },
    { formatter: ({ row }: { row: Schedule }) => <span className="tabular-nums">{row.stats.critical}</span>, for: ['critical'] },
    {
      formatter: ({ row }: { row: Schedule }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openDetail(row)}>{t('Common_open')}</DropdownMenuItem>
              {canEdit && row.status !== 'ARCHIVED' && <DropdownMenuItem onClick={() => { setEditing(row); setFormOpen(true) }}><Pencil className="h-4 w-4 mr-2" />{t('Common_edit')}</DropdownMenuItem>}
              {canAdmin && row.status !== 'ARCHIVED' && <DropdownMenuItem onClick={() => setPending({ kind: 'archive', schedule: row })}><Archive className="h-4 w-4 mr-2" />{t('Schedule_action_archive')}</DropdownMenuItem>}
              {canAdmin && row.status === 'ARCHIVED' && <DropdownMenuItem onClick={() => setPending({ kind: 'restore', schedule: row })}><ArchiveRestore className="h-4 w-4 mr-2" />{t('Schedule_action_restore')}</DropdownMenuItem>}
              {canDelete && (<><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive" onClick={() => setPending({ kind: 'delete', schedule: row })}><Trash2 className="h-4 w-4 mr-2" />{t('Common_delete')}</DropdownMenuItem></>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      for: ['actions'],
    },
  ]

  if (!projectId || (loading && !data)) return <ScheduleListSkeleton />
  const total = data?.schedules?.length ?? 0

  return (
    <div>
      <div className="py-2 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="font-bold text-lg">{total}</span>
          <span className="text-muted-foreground text-sm">{t('Common_total')}</span>
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="h-8 w-auto min-w-[150px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('Common_all')}</SelectItem>
              <SelectItem value="DRAFT">{t('Schedule_status_DRAFT')}</SelectItem>
              <SelectItem value="ACTIVE">{t('Schedule_status_ACTIVE')}</SelectItem>
              {includeArchived && <SelectItem value="ARCHIVED">{t('Schedule_status_ARCHIVED')}</SelectItem>}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch id="include-archived" checked={includeArchived} onCheckedChange={(v) => { setIncludeArchived(v); if (!v && statusFilter === 'ARCHIVED') setStatusFilter('all') }} />
            <Label htmlFor="include-archived" className="text-sm">{t('Schedule_include_archived')}</Label>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder={t('Common_search')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-[180px] pl-8 text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canAdmin && <Button variant="outline" size="sm" className="h-8" onClick={() => setSettingsOpen(true)}><Settings className="h-4 w-4" /></Button>}
          {canCreate && <Button variant="outline" size="sm" className="h-8" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" />{t('Schedule_import')}</Button>}
          {canCreate && <Button size="sm" className="h-8" onClick={() => { setEditing(null); setFormOpen(true) }}><Plus className="h-4 w-4" />{t('Schedule_new')}</Button>}
        </div>
      </div>

      {total === 0 ? (
        <Card><CardContent className="py-12 flex flex-col items-center gap-3 text-center">
          <p className="text-muted-foreground">{t('Schedule_empty')}</p>
          {canCreate && <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" />{t('Schedule_import')}</Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true) }}><Plus className="h-4 w-4" />{t('Schedule_new')}</Button>
          </div>}
        </CardContent></Card>
      ) : (
        <Grid columns={columns} rows={rows} formatters={formatters} showHeader sorting={false} noDataMessage={t('Schedule_no_match')} onRowClick={openDetail} />
      )}

      <ScheduleFormDialog open={formOpen} onOpenChange={setFormOpen} projectId={projectId} schedule={editing ?? undefined} onSaved={(s) => { if (!editing) openDetail(s) }} />
      {importOpen && <ScheduleImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={projectId} />}
      {settingsOpen && <ScheduleSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} projectId={projectId} />}
      <ConfirmDialog
        open={!!pending}
        title={pending ? t(`Schedule_confirm_${pending.kind}_title`) : ''}
        description={pending ? t(`Schedule_confirm_${pending.kind}_body`, { name: pending.schedule.name, count: pending.schedule.activityCount }) : undefined}
        confirmLabel={pending ? t(`Schedule_confirm_${pending.kind}_action`) : ''}
        variant={pending?.kind === 'delete' ? 'destructive' : 'default'}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={runPending}
      />
    </div>
  )
}
export default Schedules
